/* oxlint-disable typescript/no-explicit-any -- descriptors inspect flexible schema defs */
/* oxlint-disable typescript/no-unsafe-argument, typescript/no-unsafe-assignment, typescript/no-unsafe-call, typescript/no-unsafe-member-access, typescript/no-unsafe-return -- schema defs are intentionally dynamic */
import type * as types from './types'

const MAX_FIELD_TAG = 536_870_911
const RESERVED_FIELD_TAG_START = 19_000
const RESERVED_FIELD_TAG_END = 19_999

export function withField<T extends types.Schema>(schema: T, tag: number): T {
  assertValidFieldTag(tag)
  const def = schema._zod.def
  return new schema._zod.constr({
    ...def,
    proto: {
      ...def.proto,
      field: { tag },
    },
  })
}

export function assertValidFieldTag(tag: number) {
  if (!Number.isInteger(tag) || tag <= 0 || tag > MAX_FIELD_TAG) {
    throw new Error(
      `Protobuf field tag must be a positive integer <= ${MAX_FIELD_TAG}`,
    )
  }

  if (tag >= RESERVED_FIELD_TAG_START && tag <= RESERVED_FIELD_TAG_END) {
    throw new Error(
      `Protobuf field tag must not be in reserved range ${RESERVED_FIELD_TAG_START}..${RESERVED_FIELD_TAG_END}`,
    )
  }
}

export function describe(schema: types.Schema): types.SchemaDescriptor {
  return describeDef(schema._zod.def)
}

function describeDef(def: types.SchemaTypeDef): types.SchemaDescriptor {
  const descriptor: types.SchemaDescriptor = { type: def.type }

  if (def.meta) descriptor.metadata = { ...def.meta }
  if (def.proto) descriptor.proto = { ...def.proto }

  switch (def.type) {
    case 'object':
      descriptor.shape = {}
      for (const key in def.shape) {
        if (!Object.hasOwn(def.shape, key)) continue
        descriptor.shape[key] = describe(def.shape[key])
      }
      break
    case 'array':
      descriptor.element = describe(def.element)
      break
    case 'record':
      descriptor.key = describe(def.keySchema)
      descriptor.value = describe(def.valueSchema)
      break
    case 'enum':
      descriptor.values = def.values
      break
    case 'literal':
      descriptor.literal = def.value
      break
    case 'union':
      descriptor.options = def.options.map((option: types.Schema) =>
        describe(option),
      )
      break
    case 'discriminated_union':
      descriptor.discriminator = def.discriminator
      descriptor.options = def.options.map((option: types.Schema) =>
        describe(option),
      )
      break
    case 'optional':
    case 'nullable':
      descriptor.wrapper = def.type
      descriptor.schema = describe(def.schema)
      break
    case 'default':
      descriptor.wrapper = 'default'
      descriptor.schema = describe(def.schema)
      descriptor.default =
        typeof def.defaultValue === 'function'
          ? { hasValue: true, factory: true }
          : { hasValue: true, value: def.defaultValue }
      break
    case 'catch':
      descriptor.wrapper = 'catch'
      descriptor.schema = describe(def.schema)
      descriptor.catch =
        typeof def.catchValue === 'function'
          ? { hasValue: true, factory: true }
          : { hasValue: true, value: def.catchValue }
      break
  }

  return descriptor
}
